<template>
    <div class="legal-people-page">
        <a-spin tip="Carregando..." :spinning="spinning">
            <a-config-provider :locale="currentLocale">
                <a-card>
                    <template #title>
                        <a-breadcrumb separator=">">
                            <a-breadcrumb-item><span>Pessoas Jurídicas</span></a-breadcrumb-item>
                            <a-breadcrumb-item>Listar</a-breadcrumb-item>
                        </a-breadcrumb>
                    </template>
                    <template #extra>
                        <a-button :icon="h(PlusOutlined)" @click="openModal()">Novo Registro</a-button>
                    </template>

                    <div class="table-toolbar">
                        <a-input-search
                            v-model:value="search"
                            placeholder="Pesquisar por razão social ou CNPJ"
                            allow-clear
                            :enter-button="false"
                            style="max-width: 320px"
                            @search="load"
                        />
                    </div>

                    <a-table
                        :columns="columns"
                        :data-source="dataSource"
                        :row-key="(r) => r.id"
                        bordered
                    >
                        <template #bodyCell="{ column, record }">
                            <template v-if="column.key === 'acao'">
                                <a-tooltip title="Alterar">
                                    <a-button type="primary" class="center-flex" shape="circle" @click="openModal(record)">
                                        <template #icon><FormOutlined /></template>
                                    </a-button>
                                </a-tooltip>
                                <a-popconfirm
                                    :title="'Excluir \'' + (record.company_name || '') + '\'?'"
                                    ok-text="Excluir"
                                    cancel-text="Cancelar"
                                    :ok-button-props="{ danger: true }"
                                    @confirm="destroy(record)"
                                >
                                    <template #icon><QuestionCircleOutlined style="color: #ff4d4f" /></template>
                                    <a-tooltip title="Excluir">
                                        <a-button type="primary" class="center-flex" danger shape="circle">
                                            <template #icon><DeleteOutlined /></template>
                                        </a-button>
                                    </a-tooltip>
                                </a-popconfirm>
                            </template>
                        </template>
                    </a-table>
                </a-card>
                <a-modal
                    v-model:open="modalVisible"
                    :title="editingId ? 'Editar Pessoa Jurídica' : 'Nova Pessoa Jurídica'"
                    :mask-closable="false"
                    width="560px"
                    :z-index="10000"
                    @ok="submitForm"
                    @cancel="closeModal"
                >
                    <a-form layout="vertical" :model="form">
                        <a-form-item label="Razão social" required>
                            <a-input v-model:value="form.company_name" placeholder="Razão social" />
                        </a-form-item>
                        <a-form-item label="CNPJ" required>
                            <a-input v-model:value="form.cnpj" placeholder="Somente números (14 dígitos)" :maxlength="14" />
                        </a-form-item>
                        <a-form-item label="Nome fantasia">
                            <a-input v-model:value="form.trade_name" placeholder="Nome fantasia (opcional)" />
                        </a-form-item>
                        <a-form-item label="Inscrição estadual">
                            <a-input v-model:value="form.state_registration" placeholder="Opcional" />
                        </a-form-item>
                        <a-form-item label="Inscrição municipal">
                            <a-input v-model:value="form.municipal_registration" placeholder="Opcional" />
                        </a-form-item>
                        <a-form-item label="E-mail">
                            <a-input v-model:value="form.email" placeholder="E-mail (opcional)" />
                        </a-form-item>
                        <a-form-item label="Telefone">
                            <a-input v-model:value="form.phone" placeholder="Telefone (opcional)" />
                        </a-form-item>
                        <a-form-item label="Ativo">
                            <a-switch v-model:checked="form.active" />
                        </a-form-item>
                    </a-form>
                </a-modal>
            </a-config-provider>
        </a-spin>
    </div>
</template>

<script setup>
import { ref, h, onMounted } from 'vue';
import { PlusOutlined, FormOutlined, DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons-vue';
import ptBR from 'ant-design-vue/es/locale/pt_BR';
import { message } from 'ant-design-vue';

const route = '/api/register/legal-person';
const currentLocale = ref(ptBR);
const spinning = ref(false);
const search = ref('');
const dataSource = ref([]);
const modalVisible = ref(false);
const editingId = ref(null);

const form = ref({
    company_name: '',
    cnpj: '',
    trade_name: '',
    state_registration: '',
    municipal_registration: '',
    email: '',
    phone: '',
    active: true,
});

const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 70 },
    { title: 'Razão social', dataIndex: 'company_name', key: 'company_name', ellipsis: true },
    { title: 'CNPJ', dataIndex: 'cnpj', key: 'cnpj', width: 140 },
    { title: 'Nome fantasia', dataIndex: 'trade_name', key: 'trade_name', ellipsis: true },
    { title: 'E-mail', dataIndex: 'email', key: 'email', ellipsis: true },
    { title: 'Telefone', dataIndex: 'phone', key: 'phone', width: 120 },
    { title: 'Ação', key: 'acao', width: 120 },
];

function openModal(record = null) {
    editingId.value = record ? record.id : null;
    form.value = {
        company_name: record ? (record.company_name ?? '') : '',
        cnpj: record ? (record.cnpj ?? '') : '',
        trade_name: record ? (record.trade_name ?? '') : '',
        state_registration: record ? (record.state_registration ?? '') : '',
        municipal_registration: record ? (record.municipal_registration ?? '') : '',
        email: record ? (record.email ?? '') : '',
        phone: record ? (record.phone ?? '') : '',
        active: record ? !!record.active : true,
    };
    modalVisible.value = true;
}

function closeModal() {
    modalVisible.value = false;
    editingId.value = null;
    form.value = {
        company_name: '', cnpj: '', trade_name: '', state_registration: '', municipal_registration: '',
        email: '', phone: '', active: true,
    };
}

async function load() {
    try {
        spinning.value = true;
        const params = search.value?.trim() ? { search: search.value.trim() } : {};
        const { data } = await window.axios.get(route, { params });
        dataSource.value = data?.data ?? [];
    } catch (err) {
        message.error(err.response?.data?.message || 'Erro ao carregar.');
    } finally {
        spinning.value = false;
    }
}

async function submitForm() {
    const cnpj = String(form.value.cnpj || '').replace(/\D/g, '');
    if (!form.value.company_name?.trim()) {
        message.warning('Informe a razão social.');
        return;
    }
    if (cnpj.length !== 14) {
        message.warning('CNPJ deve ter 14 dígitos.');
        return;
    }
    try {
        spinning.value = true;
        const payload = {
            company_name: form.value.company_name.trim(),
            cnpj,
            trade_name: form.value.trade_name?.trim() || null,
            state_registration: form.value.state_registration?.trim() || null,
            municipal_registration: form.value.municipal_registration?.trim() || null,
            email: form.value.email?.trim() || null,
            phone: form.value.phone?.trim() || null,
            active: !!form.value.active,
        };
        if (editingId.value) {
            await window.axios.put(`${route}/${editingId.value}`, payload);
            message.success('Alterado com sucesso!');
        } else {
            await window.axios.post(route, payload);
            message.success('Criado com sucesso!');
        }
        closeModal();
        await load();
    } catch (err) {
        const msg = err.response?.data?.message || err.response?.data?.errors?.cnpj?.[0] || 'Erro ao salvar.';
        message.error(msg);
    } finally {
        spinning.value = false;
    }
}

async function destroy(record) {
    try {
        spinning.value = true;
        await window.axios.delete(`${route}/${record.id}`);
        message.success('Excluído com sucesso!');
        await load();
    } catch (err) {
        message.error(err.response?.data?.message || 'Erro ao excluir.');
    } finally {
        spinning.value = false;
    }
}

onMounted(() => load());
</script>

<style scoped>
.legal-people-page {
    padding: 24px;
}
.table-toolbar {
    margin-bottom: 16px;
}
.center-flex {
    display: inline-flex;
    align-items: center;
    justify-content: center;
}
</style>
